import React, { useState } from 'react';
import _ from 'lodash';
import { formatFullDateTime, waiverDatesListToPrettyDateFilter } from "../lib/filters";
import { Confirmation } from "../lib/ModalService";
import WaiverService from "./WaiverService";
import WaiverActions from "./WaiverActions";
import { Waiver } from '../types/waiver';
import { WAIVER_STATUS_ACTIVE, WAIVER_STATUS_CANCELLED, WAIVER_STATUS_PENDING } from '../constants';

interface User {
  accountId: string;
  id: string;
}

interface WaiverListRowProps {
  waiver: Waiver;
  canEditWaivers: () => boolean;
  canViewAllWaivers: () => boolean;
  user: User;
  onWaiverChange: () => void;
}

const WaiverListRow: React.FC<WaiverListRowProps> = ({
  waiver,
  canEditWaivers,
  canViewAllWaivers,
  user,
  onWaiverChange,
}) => {
  const [statusHover, setStatusHover] = useState(false);
  const [confirmState, setConfirmState] = useState({isOpen: false, title: '', message: '', previousStatus: ''});

  const waiverRegions = (maxRegions?: number): string => {
    let regions: string[];
    if (waiver.systemWide) {
      regions = ['SYSTEM-WIDE'];
    } else if (!_.isEmpty(waiver.countryCodes)) {
      regions = waiver.countryCodes.sort();
    } else {
      regions = _.sortBy(waiver.airportCodes);
    }

    if (maxRegions && regions.length > maxRegions) {
      return regions.slice(0, maxRegions).join(', ') + '...';
    }
    return regions.join(', ');
  };

  const transitionWaiverState = (event: React.MouseEvent<HTMLTableCellElement>) => {
    if (!canEditWaivers()) {
      return Promise.resolve();
    }
    event.stopPropagation();
    const stateChangeAction = getWaiverStatusChangeText();
    const title = `${stateChangeAction} Waiver`;
    const message = `Are you sure you want to ${stateChangeAction.toLowerCase()} this waiver?`;
    setConfirmState({title, message, previousStatus: waiver.status, isOpen: true});
  };

  const getWaiverStatusChangeText = (): string => {
    switch (waiver.status) {
      case WAIVER_STATUS_PENDING:
        return 'Activate';
      case WAIVER_STATUS_CANCELLED:
        return 'Reactivate';
      case WAIVER_STATUS_ACTIVE:
        return 'Cancel';
      default:
        return '';
    }
  };

  const getNextWaiverStatus = (): string => {
    switch (waiver.status) {
      case 'PENDING':
      case 'CANCELLED':
        return 'ACTIVE';
      default:
        return 'CANCELLED';
    }
  };

  const waiverStatusText = (): string => {
    if (statusHover && canEditWaivers()) {
      return getWaiverStatusChangeText();
    }
    return WaiverActions.waiverStatusDescription(waiver);
  };

  const hasWaiverDocument = (): boolean => {
    return WaiverActions.hasWaiverDocument(waiver);
  };

  const waiverUrl = (): string => {
    return WaiverActions.waiverUrl(waiver);
  };

  const openDocument = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    window.open(WaiverActions.documentLink(waiver), '_blank');
  };

  const goToWaiverTripSearch = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    window.open(`/trips/search?waiver=${waiver.id}`, '_blank');
  };

  return (<>
    <tr>
      <td className="waiver-issuing-airline col-md-1">
        {waiver.issuingAirlineCode}
        <br />
        {waiver.code}
      </td>
      <td className="waiver-name col-md-2">
        <a href={waiverUrl()} className="record-locator-base">
          {waiver.name}
        </a>
      </td>
      <td className="waiver-issue-date col-md-1">{waiver.publishedDate}</td>
      <td className="waiver-travel-dates col-md-2">
        {waiverDatesListToPrettyDateFilter(waiver)}
      </td>
      <td className="waiver-airports col-md-2" title={waiverRegions()}>
        {waiverRegions(8)}
      </td>
      {canEditWaivers() && (
        <td
          className={`waiver-status col-md-1 ${
            waiver.status === 'PENDING' ? 'inactive' : 'actionable'
          }`}
          onMouseOver={() => setStatusHover(true)}
          onMouseLeave={() => setStatusHover(false)}
          onClick={transitionWaiverState}
        >
          {waiverStatusText()}
        </td>
      )}
      <td className="waiver-last-modified-date col-md-1">
        {formatFullDateTime(waiver.acquisitionDateTimeUtc)}
      </td>
      {canViewAllWaivers() && (
        <td className="col-md-1">
          <div
            className="waiver-affected-trips sprites"
            title="Find all trips affected by this waiver"
            onClick={goToWaiverTripSearch}
          ></div>
        </td>
      )}
      <td className="col-md-1">
        {hasWaiverDocument() && (
          <div
            className="waiver-document sprites"
            onClick={openDocument}
          ></div>
        )}
      </td>
    </tr>
    {confirmState.isOpen && <Confirmation
      message={confirmState.message}
      optionOverrides={{
        title: confirmState.title,
        message2: waiver.name,
        onSubmit: () => {
          waiver.updatedByAccountId = user.accountId;
          waiver.updatedByUserId = user.id;
          waiver.status = getNextWaiverStatus();
          return WaiverService.upsertWaiver(waiver)
            .then(() => {
              onWaiverChange();
          }).catch((error: any) => {
            waiver.status = confirmState.previousStatus;
          }).finally(() => {
            setConfirmState({isOpen: false, title: '', message: '', previousStatus: ''});
          });
        }, cancel: () => setConfirmState({isOpen: false, title: '', message: '', previousStatus: ''})
      }}
    />}
  </>);
};

export default WaiverListRow;